import { tz } from '@date-fns/tz';
import type { TimeZone } from '@iheartjane/types';
import { timezone as parseApiTimezones } from '@iheartjane/utils';
import {
  differenceInCalendarDays,
  format,
  formatISO,
  isValid,
  parse,
  parseISO,
} from 'date-fns';
import type { ContextFn } from 'date-fns';

import type { Time } from '@jane/shared/types';

const INVALID_DATE = 'Invalid Date';

export interface CalendarFormats {
  lastDay: string;
  lastWeek: string;
  nextDay: string;
  nextWeek: string;
  sameDay: string;
  sameElse: string;
}

interface FormatTimeParams {
  /** A date-fns parse format, ex `yyyy-MM-dd` or `t` for unix seconds */
  inputFormat?: string;
  /** A date-fns format; defaults to ISO 8601 */
  outputFormat?: string;
  time: Time;
  timezone?: TimeZone;
}

function toDate(
  time: Time,
  inputFormat?: string,
  context?: ContextFn<Date>
): Date {
  const options = { in: context };
  if (time instanceof Date) return context ? context(time) : time;
  if (inputFormat) return parse(String(time), inputFormat, new Date(), options);
  if (typeof time === 'number') {
    return context ? context(time) : new Date(time);
  }

  const isoDate = parseISO(time, options);
  if (isValid(isoDate)) return isoDate;

  // Fall back to the native parser for non-ISO strings, ex `2024-10-22 20:26:48 UTC`
  const nativeDate = new Date(time);
  return context ? context(nativeDate) : nativeDate;
}

function toZonedDate(date: Date, timezone?: TimeZone): Date {
  if (!timezone) return date;
  return tz(parseApiTimezones(timezone))(date);
}

function formatDate(date: Date, outputFormat?: string): string {
  if (!isValid(date)) return INVALID_DATE;
  return outputFormat ? format(date, outputFormat) : formatISO(date);
}

export function formatTime({
  time,
  outputFormat,
  inputFormat,
  timezone,
}: FormatTimeParams): string {
  const date = toZonedDate(toDate(time, inputFormat), timezone);
  return formatDate(date, outputFormat);
}

export function formatUtcTime(
  time: Time,
  outputFormat?: string,
  inputFormat?: string
): string {
  return formatDate(toDate(time, inputFormat, tz('UTC')), outputFormat);
}

// NB: mirrors dayjs' calendar plugin, which compares the date's wall-clock day
// (in its own timezone) against *local* today.
function getCalendarFormatKey(date: Date): keyof CalendarFormats {
  const wallClockDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const daysFromToday = differenceInCalendarDays(wallClockDay, new Date());

  if (daysFromToday < -6) return 'sameElse';
  if (daysFromToday < -1) return 'lastWeek';
  if (daysFromToday < 0) return 'lastDay';
  if (daysFromToday < 1) return 'sameDay';
  if (daysFromToday < 2) return 'nextDay';
  if (daysFromToday < 7) return 'nextWeek';
  return 'sameElse';
}

export function formatWithCalendar(
  time: Time,
  timezone: TimeZone,
  calendarFormats: CalendarFormats,
  inputFormat?: string
): string {
  const date = toZonedDate(toDate(time, inputFormat), timezone);
  if (!isValid(date)) return INVALID_DATE;
  return format(date, calendarFormats[getCalendarFormatKey(date)]);
}
